import { useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';

import {
  recommendationPagesWithNewsletter,
  RecommendationPageType,
} from '../config';
import { FollowRecommendationPageDocument } from '../graphql/follow-recommendation-page.gql-types';
import { UnfollowRecommendationPageDocument } from '../graphql/unfollow-recommendation-page.gql-types';

type UseSetFollowStatusResult = {
  isFollowing: boolean;
  isUpdating: boolean;
  hasError: boolean;
  toggleFollow: () => void;
  resetError: () => void;
};

export const useSetFollowStatus = (
  pageId: string,
  isFollowed: boolean,
  pageType: RecommendationPageType
): UseSetFollowStatusResult => {
  const [isFollowing, setIsFollowing] = useState(isFollowed);
  const [hasError, setHasError] = useState(false);
  const hasSubscription = recommendationPagesWithNewsletter.includes(pageType)
    ? { hasSubscription: true }
    : {};

  const [setFollowState, { loading: isUpdatingFollow }] = useMutation(
    FollowRecommendationPageDocument,
    {
      variables: { pageId, ...hasSubscription },
      onCompleted: (data) => {
        if (data?.contentPageFollow?.error) {
          setHasError(true);
        } else {
          setIsFollowing(true);
        }
      },
      onError: () => {
        setHasError(true);
      },
    }
  );

  const [setUnfollowState, { loading: isUpdatingUnfollow }] = useMutation(
    UnfollowRecommendationPageDocument,
    {
      variables: { pageId },
      onCompleted: (data) => {
        if (data?.contentPageUnfollow?.error) {
          setHasError(true);
        } else {
          setIsFollowing(false);
        }
      },
      onError: () => {
        setHasError(true);
      },
    }
  );

  const toggleFollow = useCallback((): void => {
    if (isFollowing) {
      setUnfollowState();
    } else {
      setFollowState();
    }
  }, [isFollowing, setUnfollowState, setFollowState]);

  const resetError = useCallback(() => {
    setHasError(false);
  }, []);

  return {
    isFollowing,
    isUpdating: isUpdatingFollow || isUpdatingUnfollow,
    hasError,
    toggleFollow,
    resetError,
  };
};
